/**
 * LandingTest10 - AI Generated Landing Page
 * This is a preview page - no auto-redirect for logged-in users
 */
import React from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import SEOHelmet from '../../components/SEOHelmet';

interface Section {
  title: string;
  text: string;
}

const LandingTest10: React.FC = () => {
  usePageTitle('Landing Test - CEC Track');

  // Static demo data
  const pageData = {
    pageTitle: 'CEC Track - Professional License Management',
    heroSection: {
      title: 'Manage Your Professional Licenses with Ease',
      text: 'Track your continuing education credits, manage multiple licenses, and stay compliant across all states.'
    },
    featureList: [
      {
        title: 'Multi-State Management',
        text: 'Track licenses and requirements across multiple states from one centralized dashboard.'
      },
      {
        title: 'Automatic Tracking',
        text: 'Upload certificates and we automatically calculate your progress toward renewal requirements.'
      },
      {
        title: 'Smart Notifications',
        text: 'Get timely reminders about upcoming deadlines and requirements to stay ahead of renewals.'
      }
    ],
    steps: [
      {
        title: 'Step 1: Add Your Licenses',
        text: 'Enter your professional license information for each state where you are licensed.'
      },
      {
        title: 'Step 2: Upload Certificates',
        text: 'Upload your continuing education certificates as you complete courses.'
      },
      {
        title: 'Step 3: Track Progress',
        text: 'Monitor your progress in real-time and receive notifications about upcoming deadlines.'
      }
    ],
    professionalsSupported: [
      'Architects',
      'Engineers',
      'Real Estate Professionals',
      'Healthcare Providers',
      'Accountants'
    ]
  };

  return (
    <div className="container py-5">
      <SEOHelmet 
        title="[Design Preview] Landing Test - CEC Track" 
        description="Internal design preview - not for production"
        noindex={true}
      />

      {/* Hero Section */}
      <div className="row mb-5">
        <div className="col text-center">
          <h1 className="display-4 mb-3">{pageData.heroSection.title}</h1>
          <p className="lead">{pageData.heroSection.text}</p>
        </div>
      </div>

      {/* Features Section */}
      <div className="row mb-5">
        <div className="col-12 mb-4">
          <h2 className="text-center">Key Features</h2>
        </div>
        {pageData.featureList.map((feature: Section) => (
          <div className="col-md-4 mb-3" key={feature.title}>
            <div className="card h-100">
              <div className="card-body">
                <h3 className="card-title h5">{feature.title}</h3>
                <p className="card-text">{feature.text}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Steps Section */}
      <div className="row mb-5">
        <div className="col-12 mb-4">
          <h2 className="text-center">How It Works</h2>
        </div>
        {pageData.steps.map((step: Section, index) => (
          <div className="col-md-4 mb-3" key={step.title}>
            <div className="text-center">
              <div className="display-6 text-primary mb-3">{index + 1}</div>
              <h3 className="h5">{step.title}</h3>
              <p>{step.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Professionals Supported Section */}
      <div className="row">
        <div className="col-12 mb-4">
          <h2 className="text-center">Professionals We Support</h2>
        </div>
        <div className="col-12">
          <div className="d-flex flex-wrap justify-content-center gap-3">
            {pageData.professionalsSupported.map((professional: string) => (
              <div className="badge bg-primary p-3" key={professional}>
                {professional}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingTest10;